import { type ReactNode, useMemo } from 'react'

import {
  Badge,
  Box,
  Flex,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'

import { Button } from '@/components/ui'
import { VideoPlayer } from '@/features/video-player'
import {
  GridAnalysisJob,
  TransitionDirection,
} from '@/graphql/generated/schemas'

import { GridModalHeader } from '../../components/GridModalHeader'
import { scrollbarStyle } from '../../styles/styles'
import { IncidentOptionI } from '../../types/types'

interface DirectionBadgeIProps {
  isMatch: boolean
}

const DirectionBadge = ({ isMatch }: DirectionBadgeIProps) => (
  <Badge colorScheme={isMatch ? 'green' : 'red'}>
    {isMatch ? 'Match' : 'Mismatch'}
  </Badge>
)

const TransitionItem = ({ children }: { children: ReactNode }) => (
  <Box
    bg='gray.50'
    border='1px solid'
    borderColor='gray.200'
    borderRadius='md'
    px={3}
    py={2}
  >
    {children}
  </Box>
)

interface GridTestSuccessCardIProps {
  job?: GridAnalysisJob
  labeledIn: number
  labeledOut: number
  isSaving: boolean
  onClose: () => void
  onSelectAnotherIncident: () => void
  onSaveGrid?: () => void
  selectedIncident: IncidentOptionI | null
}

export const GridTestSuccessCard = ({
  job,
  labeledIn,
  labeledOut,
  isSaving,
  onClose,
  onSelectAnotherIncident,
  onSaveGrid,
  selectedIncident,
}: GridTestSuccessCardIProps) => {
  const transitions = job?.transitions ?? []
  const [computedIn, computedOut] = useMemo(
    () => [
      transitions.filter((t) => t.direction === TransitionDirection.In).length,
      transitions.filter((t) => t.direction === TransitionDirection.Out).length,
    ],
    [transitions]
  )
  const isInMatch = computedIn === labeledIn
  const isOutMatch = computedOut === labeledOut

  return (
    <>
      <ModalHeader p={0} w='100%'>
        <GridModalHeader
          subtitle={`Review the test grid's results for ${selectedIncident?.incidentId}`}
          title='Grid Testing Results'
        />
      </ModalHeader>
      <ModalBody p={0}>
        {job?.annotatedVideoUrl && (
          <Box borderRadius='xl' mt={4} overflow='hidden'>
            <VideoPlayer
              showControls
              showToggleLiveButton={false}
              url={job.annotatedVideoUrl}
            />
          </Box>
        )}

        <Box
          border='1px solid'
          borderColor='gray.200'
          borderRadius='lg'
          mt={4}
          overflow='hidden'
        >
          <Table size='sm' sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr bg='gray.50'>
                <Th fontSize='sm' width='25%'>
                  Direction
                </Th>
                <Th fontSize='sm' width='25%'>
                  Expected
                </Th>
                <Th fontSize='sm' width='25%'>
                  Computed
                </Th>
                <Th fontSize='sm' width='25%'>
                  Result
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td fontSize='sm' fontWeight='bold' py={2}>
                  IN
                </Td>
                <Td fontSize='sm' py={2}>
                  {labeledIn}
                </Td>
                <Td fontSize='sm' py={2}>
                  {computedIn}
                </Td>
                <Td py={2}>
                  <DirectionBadge isMatch={isInMatch} />
                </Td>
              </Tr>
              <Tr>
                <Td fontSize='sm' fontWeight='bold' py={2}>
                  OUT
                </Td>
                <Td fontSize='sm' py={2}>
                  {labeledOut}
                </Td>
                <Td fontSize='sm' py={2}>
                  {computedOut}
                </Td>
                <Td py={2}>
                  <DirectionBadge isMatch={isOutMatch} />
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Text fontSize='md' fontWeight='bold' mt={5}>
          People Detected
        </Text>
        <Flex
          direction='column'
          gap={1.5}
          maxH='180px'
          mt={2}
          overflowY='auto'
          sx={scrollbarStyle}
        >
          {transitions.length === 0 ? (
            <TransitionItem>
              <Text color='gray.500' fontSize='sm'>
                No transitions detected
              </Text>
            </TransitionItem>
          ) : (
            transitions.map((t, i) => (
              <TransitionItem key={`${t.personId}-${i}`}>
                <Text fontSize='sm'>
                  Person #{t.personId} · {t.direction} ·{' '}
                  {t.timestamp.toFixed(2)}s
                </Text>
              </TransitionItem>
            ))
          )}
        </Flex>
      </ModalBody>
      <ModalFooter mt={6} p={0}>
        <Flex direction='row' gap={3} justifyContent='flex-end'>
          <Button mr={0} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button mr={0} onClick={onSelectAnotherIncident} variant='secondary'>
            Select Another Incident
          </Button>
          <Button isDisabled={isSaving} onClick={onSaveGrid}>
            Save New Camera Grid
          </Button>
        </Flex>
      </ModalFooter>
    </>
  )
}
